import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { actions as sessionActions, sessionSelector } from "reducers/session";
import { actions as notificationActions } from "reducers/notification";
import { actions, slotTemplateSelector } from "reducers/slotTemplateModal";
import { AppDispatch, RootState } from "store";
import { TemplateDetailModal } from "./TemplateDetailModal";
import { FrequencyType, TemplateDetailModalContainerPropsType } from "./types";
import { NotificationStateType } from "reducers/types/notification";
import { TemplateFormValues } from "../types";
import { compensateTime, formatDDMMYYYY } from "utils/date";
import { WEEK_DAYS_NAMES } from "constants/slots";
import moment from "moment";
import { DATE_FORMATS } from "utils/constants";
import "./i18n";
import { useTranslation } from "react-i18next";
import { dirtyAlert } from "utils/alerts";

const TemplateDetailModalContainer: React.FC<
  TemplateDetailModalContainerPropsType & ConnectorProps
> = (props): JSX.Element => {
  const { isOpen, templateDetail, workzones, onClose } = props;
  const { t } = useTranslation();
  const [values, setValues] = useState({} as TemplateFormValues);
  const prevForm = useRef<TemplateFormValues>({} as TemplateFormValues);
  const workzonesUpdated = useRef<boolean>(false);
  const testId = "template-detail";

  const handleClose = () => {
    const close = () => {
      onClose();
      props.resetWorkzones();
      props.setDirty(false);
      props.setActualRegion("");
    };

    if (props.dirty) {
      props.showNotification(dirtyAlert({ onCancel: close, scroll: false }));
    } else {
      close();
    }
  };

  const initialValues: TemplateFormValues = useMemo(() => {
    if (workzonesUpdated.current) return prevForm.current;

    const region = props.regions.find(
      (r) => r.id === templateDetail?.regionId
    );
    let workzone;
    if (workzones?.length > 0) {
      workzonesUpdated.current = true;
      workzone = workzones.find((w) => w.id === templateDetail?.workZoneId);
    }

    prevForm.current = {
      ...templateDetail,
      updatedAt: templateDetail?.updatedAt
        ? formatDDMMYYYY(templateDetail.updatedAt)
        : "",
      slotTime: {
        initial: templateDetail?.startAt
          ? compensateTime(templateDetail.startAt)
          : "",
        end: templateDetail?.finishAt
          ? compensateTime(templateDetail.finishAt)
          : "",
      },
      region,
      workzone,
      frequency: WEEK_DAYS_NAMES.reduce(
        (acc, curr) => ({
          ...acc,
          [curr]: templateDetail?.days[curr]?.capacity ?? null,
        }),
        {} as FrequencyType
      ),
    };

    return prevForm.current;
  }, [templateDetail, props.regions, workzones]);

  useEffect(() => {
    workzonesUpdated.current = false;
  }, [templateDetail?.id, isOpen]);

  /*   useEffect(() => {
    if (templateDetail?.region) {
      getWorkzones(templateDetail?.region.id);
    }
  }, [templateDetail?.region, getWorkzones]); */

  const onUpdateWorkzones = (region: string) => {
    if (region && region !== props.actualRegion) {
      props.getWorkzones(region);
      props.setActualRegion(region);
    }
  };

  const onSubmit = (values: TemplateFormValues) => {
    props.getTemplateConfig();
    setValues(values);
  };

  useEffect(() => {
    if (props.launchDate && isOpen) {
      const [date, hour] = props.launchDate.split(" ");
      const formattedDate = moment(date, DATE_FORMATS.shortISODate).format(
        DATE_FORMATS.extraShortDate
      );
      props.showNotification({
        id: "notification-update-template",
        level: "info",
        title: t("templateDetail:updateTemplateTitle"),
        body: t("templateDetail:updateTemplateMessage", {
          date: formattedDate,
          time: hour.substring(0, 5),
        }),
        onConfirmLabel: "global:updateConfirmation",
        onCancelLabel: "global:cancel",
        onConfirm: () => props.updateTemplate(templateDetail!.id, values),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.launchDate]);

  const deleteTemplate = () => {
    props.showNotification({
      title: t("templateDetail:deleteTitle"),
      body: t("templateDetail:deleteMessage"),
      level: "warning",
      onCancelLabel: "global:cancel",
      onConfirmLabel: "global:deleteConfirmation",
      onConfirm: () => {
        props.deleteTemplate(templateDetail!.id);
      },
    });
  };

  return (
    <TemplateDetailModal
      {...props}
      regions={props.regions}
      initialValues={initialValues}
      onClose={handleClose}
      onUpdateWorkzones={onUpdateWorkzones}
      onSubmit={onSubmit}
      deleteTemplate={deleteTemplate}
      testId={testId}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  dirty: sessionSelector(state).dirtyForm,
  workzones: slotTemplateSelector(state).workzones,
  actualRegion: slotTemplateSelector(state).actualRegion,
  templateDetail: slotTemplateSelector(state).templateDetail,
  launchDate: slotTemplateSelector(state).launchDate,
  fetchingWorkzones: slotTemplateSelector(state).fetchingWorkzones,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getWorkzones: (regionId: string) => {
    dispatch(actions.getWorkzonesFetch(regionId));
  },
  resetWorkzones: () => {
    dispatch(actions.resetWorkzones());
  },
  setActualRegion: (id: string) => {
    dispatch(actions.setActualRegion(id));
  },
  setDirty: (dirty: boolean) => {
    dispatch(sessionActions.setDirty(dirty));
  },
  showNotification: (content: NotificationStateType) => {
    dispatch(notificationActions.showNotification(content));
  },
  deleteTemplate: (id: number) => {
    dispatch(actions.deleteTemplateRequest(id));
  },
  updateTemplate: (id: number, params: TemplateFormValues) => {
    dispatch(actions.updateTemplateRequest({ id, body: params }));
  },
  getTemplateConfig: () => {
    dispatch(actions.getTemplateConfigRequest());
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ConnectorProps = ConnectedProps<typeof connector>;

export default connector(TemplateDetailModalContainer);
